/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { gridNormalPlugin } from '@veeam/components/lib/GridCore/plugins/normal';
import { gridSubheaderPlugin } from '@veeam/components/lib/GridCore/plugins/subheader';
import React, { useMemo } from 'react';
import { gridSingleSelectionPlugin } from '@veeam/components/lib/GridCore/plugins/singleSelection';
import { gridMultiSelectionPlugin } from '@veeam/components/lib/GridCore/plugins/multiSelection';
import { gridColumnShownStatePlugin } from '@veeam/components/lib/GridCore/plugins/columnShownState';
import { gridColumnResizePlugin } from '@veeam/components/lib/GridCore/plugins/columnResize/gridColumnResizePlugin';
import { gridNotificationPlugin } from '@veeam/components/lib/GridCore/plugins/notification';
import { gridColumnMovingPlugin } from '@veeam/components/lib/GridCore/plugins/columnMoving';
import { gridExpandCollapsePlugin } from '@veeam/components/lib/GridCore/plugins/expandCollapse';
import {
    GRID_EXPAND_COLLAPSE_PLUGIN_COLUMN_TYPE,
} from '@veeam/components/lib/GridCore/plugins/expandCollapse/types/GridExpandCollapsePluginColumnType';
import { gridHeaderCell } from '@veeam/components/lib/GridCore/renderers/gridHeaderCell';
import { gridNormalCell } from '@veeam/components/lib/GridCore/renderers/gridNormalCell';

import type { GridExpandCollapsePluginConfig } from '@veeam/components/lib/GridCore/plugins/expandCollapse/types/GridExpandCollapsePluginConfig';
import type { GridCorePlugin, GridCoreProps, GridColumnShownStatePluginConfig } from '@veeam/components';

// eslint-disable-next-line import/no-cycle
import { SelectionHeader } from '../components';
import { generatedIdField } from '../stores/grid-store/stores';

import type { GridStoreImpl as GridStore } from '../stores';
import type { GridProps } from '../interfaces';

export interface UseGridPluginsProps<RowDataT, TotalRowDataT, FilterT> {
    props: GridProps<RowDataT, TotalRowDataT, FilterT>;
    store: GridStore<RowDataT, TotalRowDataT, FilterT>;
    onContextMenu: (event: MouseEvent) => void;
    selectedIds: any[];
    idGetter: GridCoreProps<RowDataT>['idGetter'];
    getMetaByIndex: GridExpandCollapsePluginConfig<RowDataT>['getMetaByIndex'];
}

export const usePlugins = <RowDataT, TotalRowDataT, FilterT>({
    props,
    store,
    onContextMenu,
    selectedIds,
    idGetter,
    getMetaByIndex,
}: UseGridPluginsProps<RowDataT, TotalRowDataT, FilterT>) => {
    const empty = props.texts?.empty || 'No data to display';
    const isEmpty = store.data.length === 0;

    return useMemo(() => {
        const plugins: GridCorePlugin<RowDataT, any>[] = [];
        let isLoading: boolean;

        if (props.isLoading === true || props.isLoading === false) { // the case when we control a loading spinner by props
            isLoading = props.isLoading;
        } else {
            isLoading = store.requestStore.spinner;
        }

        plugins.push(
            gridNormalPlugin({
                isLoading,
                isEmpty,
                disabled: store.disabledStateStore.disabled,
                texts: {
                    empty: store.loading ? '' : empty,
                },
                isRowInactive: index => (
                    props.isRowInactive?.(store.pageData[index]) ?? false
                ),
                isRowDisabled: index => (
                    props.isRowDisabled?.(store.pageData[index]) ?? false
                ),
            })
        );

        if (store.selection?.field &&
            store.selection?.field !== generatedIdField &&
            store.selection?.multiple &&
            !isEmpty
        ) {
            plugins.push(
                gridSubheaderPlugin({ content: <SelectionHeader /> })
            );
        }

        if (store.selection?.field &&
            store.selection?.field !== generatedIdField &&
            store.selection?.multiple === false
        ) {
            plugins.push(
                gridSingleSelectionPlugin({
                    isSelected: (index) => {
                        const selectedItem = store.selected.length > 0 && store.selected[0];
                        if (typeof selectedItem === 'undefined') {
                            return false;
                        }

                        const item = store.pageData[index];
                        const itemId = item[store.selection.field];

                        if (props.isRowDisabled?.(item)) {
                            return false;
                        }

                        return selectedItem[store.selection.field] === itemId;
                    },
                    onSelect: (index) => {
                        const item = store.pageData[index];
                        const nextValue: RowDataT[] = [item];

                        store.selectionStore.onSelectionChange(nextValue);
                    },
                    onContextMenu: (index: number, e: React.MouseEvent) => {
                        const item = store.pageData[index];
                        const itemId = item[store.selection.field];
                        const isNotSelected = !selectedIds.includes(itemId);

                        if (isNotSelected) {
                            const nextValue: RowDataT[] = [item];

                            store.selectionStore.onSelectionChange(nextValue);
                        }

                        onContextMenu(e.nativeEvent);
                    },
                })
            );
        }

        if (store.selection?.field &&
            store.selection?.field !== generatedIdField &&
            store.selection?.multiple
        ) {
            plugins.push(
                gridMultiSelectionPlugin({
                    hasCheckbox: store.selection.checkbox ?? true,
                    selectAllEnabled: true,
                    onMultiSelect: (index, isShift: boolean) => {
                        const selected = store.selected;
                        const item = store.pageData[index];
                        const itemId = item[store.selection.field];

                        if (isShift && selected.length) {
                            const edgeItemSelectedIndex = store.pageData
                                .findIndex((item) => {
                                    const itemId = item[store.selection.field];

                                    return itemId === selected[selected.length - 1][store.selection.field];
                                });

                            const start = Math.min(edgeItemSelectedIndex, index);
                            const end = Math.max(edgeItemSelectedIndex, index);
                            let nextValue: RowDataT[] = [];

                            for (let i = start; i <= end; i++) {
                                nextValue.push(store.pageData[i]);
                            }

                            if (index > edgeItemSelectedIndex) {
                                nextValue = nextValue.reverse();
                            }

                            store.selectionStore.onSelectionChange(nextValue);

                            return;
                        }

                        if (selectedIds.includes(itemId)) {
                            const nextValue: RowDataT[] = selected.filter(item => item[store.selection.field] !== itemId);

                            store.selectionStore.onSelectionChange(nextValue);

                            return;
                        }

                        const nextValue: RowDataT[] = [...selected, item];

                        store.selectionStore.onSelectionChange(nextValue);
                    },
                    onSelect: (index) => {
                        const item = store.pageData[index];

                        const nextValue: RowDataT[] = [item];

                        store.selectionStore.onSelectionChange(nextValue);
                    },
                    onContextMenu: (index: number, e: React.MouseEvent) => {
                        const item = store.pageData[index];
                        const itemId = item[store.selection.field];
                        const isNotSelected = !selectedIds.includes(itemId);

                        if (isNotSelected) {
                            const nextValue: RowDataT[] = [item];

                            store.selectionStore.onSelectionChange(nextValue);
                        }

                        onContextMenu(e.nativeEvent);
                    },
                    onAllSelect: () => {
                        store.selectionStore.onHeaderSelectionChange();
                    },
                    isAllSelected: () => {
                        if (store.selected.length === 0) {
                            return false;
                        }

                        if (store.selection.isIndeterminate) {
                            return null;
                        }

                        return true;
                    },
                    isSelected: (index) => {
                        const selectedItem = store.selected.length > 0 && store.selected[0];

                        if (typeof selectedItem === 'undefined') {
                            return false;
                        }

                        const item = store.pageData[index];
                        const itemId = item[store.selection.field];

                        if (props.isRowDisabled?.(item)) {
                            return false;
                        }

                        return selectedIds.includes(itemId);
                    },
                    isRowDisabled: index => (
                        props.isRowDisabled?.(store.pageData[index]) ?? false
                    ),
                })
            );
        }

        if (props.toggleable !== false) {
            const menu: GridColumnShownStatePluginConfig['menu'] = store.columns.map((column, index) => {
                const shownState = !column.hidden;

                return {
                    id: column.id,
                    title: column.title,
                    value: shownState,
                    onClick: () => {
                        store.columnsStore.onColumnToggle({
                            id: column.id,
                            isShow: !shownState,
                        });
                    },
                };
            });

            plugins.push(
                gridColumnShownStatePlugin({
                    menu,
                })
            );
        }

        if (props.resizable !== false) {
            plugins.push(
                gridColumnResizePlugin({
                    onResize: (payload) => {
                        Object.entries(payload)
                            .forEach(([id, width]) => {
                                store.columnsStore.onColumnResize({
                                    id,
                                    width,
                                });
                            });
                    },
                })
            );
        }

        if (store.notificationStore.isOutdated) {
            plugins.push(
                gridNotificationPlugin({
                    onRequestClose: store.notificationStore.notificationConfig.onRequestClose,
                    content: store.notificationStore.notificationConfig.renderContent(),
                })
            );
        }

        if (props.orderable !== false) {
            plugins.push(
                gridColumnMovingPlugin({
                    onChange: (payload) => {
                        store.columnsStore.onColumnOrder(payload);
                    },
                })
            );
        }

        if (props.expandable) {
            plugins.push(
                gridExpandCollapsePlugin({
                    columnType: props.expandable.columnType ?? GRID_EXPAND_COLLAPSE_PLUGIN_COLUMN_TYPE.embeddedCell,
                    loadingRowTitle: props.expandable.loadingRowTitle,
                    onClick: (index: number) => {
                        const id = idGetter(store.pageData[index]) as string;

                        store.expandedRowsStore.setExpanded(id, index);
                    },
                    getMetaByIndex,
                    cellRenderer: (cellProps, meta) => {
                        const groupingKey = store.expandedRowsStore.grouping[meta.depth];
                        const value: unknown = cellProps.rowData[groupingKey] || cellProps.rowData[store.expandedRowsStore.grouping[meta.depth - 1]];

                        return gridNormalCell({
                            value: () => props.expandable.cellRenderer(value, cellProps.rowData, meta),
                        })(cellProps);
                    },
                    groupColumnConfig: props.expandable.groupColumnConfig ? {
                        ...props.expandable.groupColumnConfig,
                        header: gridHeaderCell({
                            title: props.expandable.groupColumnConfig.title,
                        }),
                    } : undefined,
                })
            );
        }

        return plugins;
    }, [
        store.columns.map(column => column.hidden).join(),
        selectedIds.join(),
        empty,
        store.loading,
        props.isLoading,
        store.requestStore.spinner,
        store.disabledStateStore.disabled,
        isEmpty,
        store.notificationStore.isOutdated,
        store.expandedRowsStore.expandedIds.join(),
    ]);
};
